import React, { useEffect, useMemo, useState } from "react";
import Button from "../../../components/Button/Button";
import { FaSave, FaTimes } from "react-icons/fa";
import { createHeures_enseignement } from "../../../services/heuresenseignementServices";
import { toast } from "react-toastify";

/** Helpers ISO week */
const getISOWeekYear = (date) => {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
  return d.getUTCFullYear();
};
const getISOWeekNumber = (date) => {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
};
const toYYYYMM = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;

const AddHoursModal = ({
  isOpen,
  onClose,
  refreshData,
  enseignants,
  heuresEnseignement = [],
}) => {
  const [selectedDate, setSelectedDate] = useState(""); // yyyy-mm-dd
  const [adherentId, setAdherentId] = useState("");
  const [heures, setHeures] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const resetForm = () => {
    setSelectedDate("");
    setAdherentId("");
    setHeures("");
    setErrorMessage("");
  };

  // Réinitialise TOUT à chaque ouverture, et met par défaut l’unique enseignant si un seul
  useEffect(() => {
    if (isOpen) {
      resetForm();
      if (Array.isArray(enseignants) && enseignants.length === 1) {
        setAdherentId(String(enseignants[0].id));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Dérivés à partir de la date
  const derived = useMemo(() => {
    if (!selectedDate) return { semaine: "", mois: "" };
    const d = new Date(`${selectedDate}T12:00:00`);
    const isoYear = getISOWeekYear(d);
    const week = String(getISOWeekNumber(d)).padStart(2, "0");
    return { semaine: `${isoYear}-W${week}`, mois: toYYYYMM(d) };
  }, [selectedDate]);

  // Doublon live
  const duplicate = useMemo(() => {
    if (!derived.semaine || !adherentId) return false;
    return heuresEnseignement.some(
      (it) => it.semaine === derived.semaine && String(it.adherent_id) === String(adherentId)
    );
  }, [derived.semaine, adherentId, heuresEnseignement]);

  useEffect(() => {
    setErrorMessage(duplicate ? "Cette semaine pour cet enseignant est déjà enregistrée." : "");
  }, [duplicate]);

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedDate) return setErrorMessage("Veuillez sélectionner une date.");
    if (!adherentId) return setErrorMessage("Veuillez sélectionner un enseignant.");
    const h = parseFloat(heures);
    if (Number.isNaN(h) || h <= 0) return setErrorMessage("Veuillez saisir un nombre d’heures valide.");
    if (duplicate) return;

    try {
      await createHeures_enseignement({
        semaine: derived.semaine,
        mois: derived.mois,
        adherent_id: adherentId,
        heures: h,
      });
      toast.success("Heures d'enseignement ajoutées avec succès.");
      handleClose();
      refreshData();
    } catch (err) {
      console.error(err);
      toast.error("Erreur lors de l'ajout des heures d'enseignement.");
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={handleClose} />

      <div className="relative mx-auto w-full max-w-xl mt-[8vh] rounded-2xl shadow-2xl overflow-hidden">
        <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-4">
          <h3 className="text-white text-lg font-bold">Ajouter des heures d’enseignement</h3>
        </div>

        <div className="bg-white dark:bg-slate-900 p-5">
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Date */}
            <div>
              <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                Date (choisir n’importe quel jour de la semaine)
              </label>
              <input
                type="date"
                autoFocus
                value={selectedDate}
                onChange={(e) => { setSelectedDate(e.target.value); setErrorMessage(""); }}
                className="input input-bordered w-full"
                required
              />
              {derived.semaine && (
                <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                  <span className="inline-flex items-center rounded-full bg-slate-100 dark:bg-slate-800 px-2 py-1 text-slate-700 dark:text-slate-200">
                    Semaine ISO : {derived.semaine}
                  </span>
                  <span className="inline-flex items-center rounded-full bg-slate-100 dark:bg-slate-800 px-2 py-1 text-slate-700 dark:text-slate-200">
                    Mois : {derived.mois}
                  </span>
                </div>
              )}
            </div>

            {/* Enseignant */}
            <div>
              <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                Enseignant
              </label>
              <select
                value={adherentId}
                onChange={(e) => { setAdherentId(e.target.value); setErrorMessage(""); }}
                className="input input-bordered w-full"
                required
              >
                <option value="">Sélectionner un enseignant</option>
                {enseignants.map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.nom} {e.prenom}
                  </option>
                ))}
              </select>
            </div>

            {/* Heures */}
            <div>
              <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                Heures
              </label>
              <input
                type="number"
                step="0.25"
                min="0"
                placeholder="Ex. 1.5"
                value={heures}
                onChange={(e) => { setHeures(e.target.value); setErrorMessage(""); }}
                className="input input-bordered w-full"
                required
              />
              <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
                Astuce : vous pouvez saisir des quarts d’heure (0.25, 0.5, 0.75…)
              </p>
            </div>

            {errorMessage && (
              <div className="text-red-500 text-sm text-center">{errorMessage}</div>
            )}

            <div className="flex justify-end gap-3 pt-2">
              <Button
                text="Annuler"
                onClick={handleClose}
                icon={FaTimes}
                type="button"
                className="gap-2 !bg-slate-100 !text-slate-800 hover:!bg-slate-200 dark:!bg-slate-800 dark:!text-slate-100 dark:hover:!bg-slate-700 !rounded-xl"
              />
              <Button
                text="Enregistrer"
                icon={FaSave}
                type="submit"
                className={`gap-2 !rounded-xl ${duplicate ? "opacity-60 pointer-events-none" : ""}`}
              />
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddHoursModal;
